import { Doctor } from "../../utils/types";
import * as Sentry from "@sentry/react";
import { apiFetch, expectJsonBody } from "../api";

export type FetchDoctorsParams = {
    page?: number;
    limit?: number;
    search?: string;
};

export type FetchDoctorsResult = {
    data: Doctor[];
    total: number;
};

/**
 * Fetch doctors for a clinic account with optional pagination and search.
 *
 * Rejects on a failed request or a malformed payload rather than resolving an empty roster: the
 * React Query callers must see a failure as `isError` and keep their last good roster (EN-1419).
 * apiFetch reports non-abort request failures to Sentry (unless a caller passes silent or
 * skipSentryOn*, which this call does not), so there is no catch here.
 */
export const fetchDoctors = async (params: FetchDoctorsParams = {}): Promise<FetchDoctorsResult> => {
    const searchParams = new URLSearchParams();
    if (params.page != null) searchParams.set("page", String(params.page));
    if (params.limit != null) searchParams.set("limit", String(params.limit));
    if (params.search?.trim()) searchParams.set("search", params.search.trim());
    const qs = searchParams.toString();
    const result = await apiFetch<FetchDoctorsResult>(`doctors${qs ? `?${qs}` : ""}`, { method: "GET" });
    if (!Array.isArray(result?.data)) {
        // apiFetch succeeded, so it reported nothing; this is the only report of a bad payload.
        const error = new Error("Malformed response from GET doctors");
        Sentry.captureException(error, { tags: { errorCategory: "code_bug", endpoint: "doctors" } });
        throw error;
    }
    return result;
};

/**
 * Create a new doctor
 * @param doctor - Doctor data
 */
export const createDoctor = async (doctor: Partial<Doctor>): Promise<Doctor> => {
    try {
        return expectJsonBody(await apiFetch<Doctor>(`doctors`, { method: "POST", json: doctor }), "POST doctors");
    } catch (error: any) {
        Sentry.captureException(error);
        throw error;
    }
};

/**
 * Update a doctor
 * @param id - Doctor ID
 * @param doctor - Updated doctor data
 */
export const updateDoctor = async (id: string, doctor: Partial<Doctor>): Promise<Doctor> => {
    try {
        return expectJsonBody(
            await apiFetch<Doctor>(`doctors/${id}`, { method: "PATCH", json: doctor }),
            `PATCH doctors/${id}`,
        );
    } catch (error: any) {
        Sentry.captureException(error);
        throw error;
    }
};

/**
 * Delete (archive) a doctor
 * @param id - Doctor ID
 */
export const deleteDoctor = async (id: string): Promise<void> => {
    try {
        await apiFetch(`doctors/${id}`, {
            method: "DELETE",
        });
    } catch (error: any) {
        Sentry.captureException(error);
        throw error;
    }
};

/**
 * Change the sort index of a doctor
 * @param doctorId - Doctor ID
 * @param sortIndex - New sort index
 */
export const changeDoctorSortIndex = async (
    doctorId: string,
    sortIndex: number,
): Promise<{ error: string } | { error: null; data: any }> => {
    try {
        return expectJsonBody(
            await apiFetch<{ error: string } | { error: null; data: any }>("doctors/sortIndex", {
                method: "PATCH",
                body: JSON.stringify({ doctorId, sortIndex }),
            }),
            "PATCH doctors/sortIndex",
        );
    } catch (error: any) {
        Sentry.captureException(error);
        return { error: "Error changing doctor sort index" };
    }
};

interface SyncCounts {
    created: number;
    updated: number;
    skipped: number;
    archived: number;
}

/**
 * Result of POST /bridge/sync-providers.
 *
 * Note what a 200 does NOT mean: a resources fetch that fails returns 200 with `partial: true`
 * and zeroed resource counts, so a caller reading only the counts reports "0 rooms" as a
 * completed import. `fetch_error` and `archival_skipped_reason` are the only signals that
 * distinguish "this clinic has no rooms" from "we couldn't ask".
 *
 * There is deliberately no `warning` field — the endpoint never sends one. `warnings` belongs to
 * POST /bridge/register, a different endpoint, and a consumer branching on `result.warning` here
 * is dead code that silently never fires.
 */
export interface SyncProvidersResult {
    doctors: SyncCounts;
    resources: SyncCounts & {
        /** The resources fetch itself failed. Counts above are zeros, not a real answer. */
        fetch_error?: boolean;
        /** Archival was skipped and why — a known-incomplete resource picture. */
        archival_skipped_reason?: string;
    };
    /** Set when any part of the sync did not complete. Always check before reporting counts. */
    partial?: boolean;
}

export const syncDoctorsFromPims = async (): Promise<SyncProvidersResult> =>
    expectJsonBody(
        await apiFetch<SyncProvidersResult>(
            "bridge/sync-providers",
            { method: "POST", timeout: 120_000 }, // 120s > 90s bridge timeout — avoids early client abort
        ),
        "POST bridge/sync-providers",
    );
